import type { LucideIcon } from 'lucide-react';
import { UsersRound, ChartColumn, Truck } from 'lucide-react';

/**
 * Level-1 global application modules.
 * Single source of truth for desktop navbar, mobile dropdown, and active detection.
 */
export type AdminModule = {
  id: string;
  label: string;
  icon: LucideIcon;
  route: string;
  /** Path prefixes that mark this module active (nested routes included). */
  match: string[];
  permission?: string;
  /** Optional submenu shown in the Settings-style dropdown. */
  children?: Array<{ label: string; to: string; permission?: string }>;
};

export const ADMIN_MODULES: AdminModule[] = [
  {
    id: 'users',
    label: 'Users',
    icon: UsersRound,
    route: '/users',
    match: [
      '/users',
      '/customers',
      '/deliverymen',
      '/vehicles',
      '/employees',
      '/roles',
      '/contacts',
      '/cashback',
      '/wallet-bonus',
    ],
  },
  {
    id: 'transactions',
    label: 'Transactions & Reports',
    icon: ChartColumn,
    route: '/transactions-reports/overview',
    match: [
      '/transactions-reports',
      '/transactions',
      '/withdrawals',
      '/withdraw-methods',
      '/disbursements',
      '/dm-earnings',
      '/reports',
    ],
  },
  {
    id: 'dispatch',
    label: 'Dispatch Management',
    icon: Truck,
    route: '/dispatch-management/live-tracking',
    match: ['/dispatch-management', '/dispatch/orders', '/dispatch/live-tracking'],
    permission: 'order',
  },
];

export function isActiveModule(mod: AdminModule, pathname: string): boolean {
  return mod.match.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function findActiveModule(pathname: string): AdminModule | undefined {
  // Prefer the longest matching prefix so nested module paths win.
  let best: AdminModule | undefined;
  let bestLen = -1;
  for (const mod of ADMIN_MODULES) {
    for (const prefix of mod.match) {
      const hit = pathname === prefix || pathname.startsWith(`${prefix}/`);
      if (hit && prefix.length > bestLen) {
        best = mod;
        bestLen = prefix.length;
      }
    }
  }
  return best;
}

export function filterModulesByPermission(
  modules: AdminModule[],
  can: (key?: string) => boolean,
): AdminModule[] {
  return modules
    .filter((m) => can(m.permission))
    .map((m) => ({
      ...m,
      children: m.children?.filter((c) => can(c.permission)),
    }));
}
